import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users, TrendingUp, ArrowLeftRight, HeartPulse, AlertCircle,
  BarChart3, PieChart, Activity, ArrowUpRight, ArrowDownRight,
  Loader2, Calendar, Landmark, UsersRound, School, GraduationCap,
  Layers, GitBranch, Building2, BookOpen
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'
import { studentsApi, apiService } from '@/services/api'
import { useAuthStore } from '@/store/authStore'

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTES LOCALSTORAGE
// ═══════════════════════════════════════════════════════════════════════════════
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
const SELECTED_SCHOOL_ID_KEY = 'lybibal-selected-school-id'
const AUTH_STORAGE_KEY = 'auth-storage'
const SELECTED_YEAR_EVENT = 'lybibal-year-change'

const getStoredSchoolId = (): number | null => {
  try {
    const sid = localStorage.getItem(SELECTED_SCHOOL_ID_KEY)
    if (sid) return Number(sid)
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      if (state?.user?.school_id) return Number(state.user.school_id)
    }
  } catch (e) { console.warn('getStoredSchoolId error:', e) }
  return null
}

const getStoredYearId = (): number | null => {
  try {
    const yid = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    if (yid) return Number(yid)
  } catch (e) { console.warn('getStoredYearId error:', e) }
  return null
}

const getStoredYearLabel = (): string | null => {
  try { return localStorage.getItem(SELECTED_YEAR_LABEL_KEY) } catch (e) { return null }
}

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════
interface StatItem { name: string; count: number; color?: string }
interface DashboardStats {
  total_students: number
  by_status: Record<string, number>
  by_sex: Record<string, number>
  social_cases: number
  handicaps: number
  by_level: StatItem[]
  by_section: StatItem[]
  by_cycle: StatItem[]
  by_class: StatItem[]
  school_id?: number
  academic_year_id?: number
}

// ═══════════════════════════════════════════════════════════════════════════════
// NORMALISATION ROBUSTE
// ═══════════════════════════════════════════════════════════════════════════════
function normalizeStats(raw: any): DashboardStats | null {
  if (!raw) return null
  const data = raw?.success === true && raw.data !== undefined ? raw.data : raw

  const byGenderRaw = data.by_sex || data.by_gender || {}
  const boysCount = byGenderRaw.M ?? byGenderRaw.m ?? byGenderRaw.Male ?? byGenderRaw.male ?? 0
  const girlsCount = byGenderRaw.F ?? byGenderRaw.f ?? byGenderRaw.Female ?? byGenderRaw.female ?? 0

  let byStatus: Record<string, number> = {}
  if (data.by_status && typeof data.by_status === 'object' && !Array.isArray(data.by_status)) {
    byStatus = data.by_status
  }

  const normalizeArray = (arr: any[]): StatItem[] => {
    if (!Array.isArray(arr)) return []
    return arr.map((item: any) => ({
      name: item.name || item.label || item.nom || item.level_name || item.section_name || item.cycle_name || item.class_name || 'Inconnu',
      count: Number(item.count ?? item.cnt ?? item.total ?? item.value ?? 0),
      color: item.color,
    })).filter((i: StatItem) => i.count > 0)
  }

  return {
    total_students: Number(data.total_students ?? data.total ?? data.totalStudents ?? data.count ?? 0),
    by_status: byStatus,
    by_sex: { M: Number(boysCount), F: Number(girlsCount) },
    social_cases: Number(data.social_cases ?? data.social_case_count ?? data.social_cases_count ?? data.socialCases ?? 0),
    handicaps: Number(data.handicaps ?? data.handicap_count ?? data.handicaps_count ?? data.handicapCount ?? data.disabilities ?? 0),
    by_level: normalizeArray(data.by_level ?? data.levels ?? data.byLevel ?? []),
    by_section: normalizeArray(data.by_section ?? data.sections ?? data.bySection ?? []),
    by_cycle: normalizeArray(data.by_cycle ?? data.cycles ?? data.byCycle ?? []),
    by_class: normalizeArray(data.by_class ?? data.classes ?? data.byClass ?? []),
    school_id: data.school_id,
    academic_year_id: data.academic_year_id,
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// COULEURS
// ═══════════════════════════════════════════════════════════════════════════════
const CHART_COLORS = [
  '#3b82f6', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6',
  '#06b6d4', '#ef4444', '#84cc16', '#f97316', '#6366f1',
  '#14b8a6', '#d946ef', '#22c55e', '#eab308', '#64748b'
]

const STATUS_COLORS: Record<string, string> = {
  active: '#22c55e', nouveau: '#3b82f6', inscrit: '#10b981',
  demission: '#f59e0b', exclu: '#ef4444', renvoye: '#f97316',
  reintegre: '#8b5cf6', transfere: '#06b6d4', ancien: '#64748b',
  suspendu: '#94a3b8'
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT GRAPHIQUE BARRES HORIZONTALES
// ═══════════════════════════════════════════════════════════════════════════════
function HorizontalBarChart({ data, maxValue, color = '#3b82f6' }: { data: StatItem[]; maxValue: number; color?: string }) {
  return (
    <div className="space-y-2 w-full">
      {data.map((item, i) => {
        const pct = maxValue > 0 ? (item.count / maxValue) * 100 : 0
        const barColor = item.color || CHART_COLORS[i % CHART_COLORS.length]
        return (
          <div key={i} className="flex items-center gap-3">
            <span className="text-xs text-gray-600 w-28 truncate text-right" title={item.name}>{item.name}</span>
            <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700 flex items-center justify-end pr-2"
                style={{ width: `${Math.max(pct, 4)}%`, backgroundColor: barColor }}
              >
                <span className="text-[10px] font-bold text-white">{item.count}</span>
              </div>
            </div>
          </div>
        )
      })}
      {data.length === 0 && (
        <div className="text-center text-gray-400 py-8 text-sm">Aucune donnée</div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT GRAPHIQUE BARRES VERTICALES
// ═══════════════════════════════════════════════════════════════════════════════
function BarChartSVG({ data, maxValue, color = '#3b82f6' }: { data: StatItem[]; maxValue: number; color?: string }) {
  const barWidth = Math.max(24, 320 / Math.max(data.length, 1) - 10)
  const chartHeight = 180
  const padding = 30
  const totalWidth = Math.max(340, data.length * (barWidth + 10) + 20)

  return (
    <svg viewBox={`0 0 ${totalWidth} ${chartHeight + padding}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
        <line key={i} x1="20" y1={chartHeight - ratio * chartHeight} x2={totalWidth - 10} y2={chartHeight - ratio * chartHeight} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 4" />
      ))}
      {data.map((item, i) => {
        const barHeight = maxValue > 0 ? (item.count / maxValue) * chartHeight : 0
        const x = i * (barWidth + 10) + 25
        const y = chartHeight - barHeight
        const barColor = item.color || CHART_COLORS[i % CHART_COLORS.length]
        return (
          <g key={i}>
            <rect x={x} y={y} width={barWidth} height={barHeight} rx={4} fill={barColor} opacity={0.85} />
            <text x={x + barWidth / 2} y={y - 6} textAnchor="middle" fontSize="11" fontWeight="600" fill="#374151">{item.count}</text>
            <text x={x + barWidth / 2} y={chartHeight + 18} textAnchor="middle" fontSize="10" fill="#6b7280">{item.name}</text>
          </g>
        )
      })}
    </svg>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT CAMEMBERT
// ═══════════════════════════════════════════════════════════════════════════════
function PieChartSVG({ data }: { data: { label: string; value: number; color: string }[] }) {
  const total = data.reduce((sum, d) => sum + d.value, 0)
  const radius = 70
  const cx = 100
  const cy = 100
  let currentAngle = 0

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        Aucune donnée
      </div>
    )
  }

  return (
    <div className="flex items-center gap-6 h-full">
      <svg viewBox="0 0 200 200" className="w-40 h-40 flex-shrink-0">
        {data.map((item, i) => {
          const angle = (item.value / total) * 360
          const startAngle = currentAngle
          const endAngle = currentAngle + angle
          currentAngle = endAngle
          const startRad = (startAngle - 90) * Math.PI / 180
          const endRad = (endAngle - 90) * Math.PI / 180
          const x1 = cx + radius * Math.cos(startRad)
          const y1 = cy + radius * Math.sin(startRad)
          const x2 = cx + radius * Math.cos(endRad)
          const y2 = cy + radius * Math.sin(endRad)
          const largeArc = angle > 180 ? 1 : 0
          return (
            <path
              key={i}
              d={`M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`}
              fill={item.color}
              stroke="white"
              strokeWidth="2"
            />
          )
        })}
        <circle cx={cx} cy={cy} r={40} fill="white" />
        <text x={cx} y={cy - 5} textAnchor="middle" fontSize="16" fontWeight="700" fill="#1f2937">{total}</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize="9" fill="#6b7280">Total</text>
      </svg>
      <div className="space-y-2 min-w-[120px]">
        {data.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
            <span className="text-gray-600 truncate">{item.label}</span>
            <span className="font-semibold text-gray-900 ml-auto">{item.value}</span>
            <span className="text-xs text-gray-400">({total > 0 ? Math.round((item.value / total) * 100) : 0}%)</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// CARTE STATISTIQUE
// ═══════════════════════════════════════════════════════════════════════════════
function StatCard({ label, value, icon: Icon, color, change, onClick, subLabel }: {
  label: string
  value: string | number
  icon: any
  color: string
  change?: string
  onClick?: () => void
  subLabel?: string
}) {
  const isPositive = change?.startsWith('+')
  const isNegative = change?.startsWith('-')
  return (
    <div onClick={onClick} className={`bg-white rounded-xl border border-gray-200 shadow-sm p-4 transition-all hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center shadow-sm`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {change && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-0.5 ${
            isPositive ? 'bg-green-100 text-green-700' : isNegative ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {isPositive ? <ArrowUpRight className="w-3 h-3" /> : isNegative ? <ArrowDownRight className="w-3 h-3" /> : null}
            {change}
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {subLabel && <p className="text-[10px] text-gray-400 mt-0.5">{subLabel}</p>}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ═══════════════════════════════════════════════════════════════════════════════
export default function StudentsDashboardPage() {
  const { lang } = useLang()
  const navigate = useNavigate()

  const [schoolId, setSchoolId] = useState<number | null>(getStoredSchoolId)
  const [academicYearId, setAcademicYearId] = useState<number | null>(getStoredYearId)
  const [academicYearName, setAcademicYearName] = useState<string>(getStoredYearLabel() || '')
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [rawResponse, setRawResponse] = useState<any>(null)

  const $t = useMemo(() => ({
    fr: {
      title: 'Tableau de bord - Inscription des élèves',
      subtitle: "Vue d'ensemble des apprenants",
      totalStudents: 'Total élèves',
      boys: 'Garçons',
      girls: 'Filles',
      byStatus: 'Répartition par statut',
      byGender: 'Répartition par genre',
      byLevel: 'Répartition par niveau',
      bySection: 'Répartition par section',
      byCycle: 'Répartition par cycle',
      byClass: 'Répartition par classe',
      transferred: 'Transférés',
      expelled: 'Renvoyés',
      socialCases: 'Cas sociaux',
      handicaps: 'Handicaps',
      loading: 'Chargement...',
      error: 'Erreur de chargement',
      distribution: 'Distribution',
      details: 'Voir les détails',
      school: 'Établissement',
      academicYear: 'Année scolaire',
      noYear: 'Aucune année sélectionnée',
      noSchool: 'Aucun établissement sélectionné',
      contextUpdated: 'Contexte scolaire mis à jour — rechargement des stats',
      classTable: 'Détail par classe',
      className: 'Classe',
      count: 'Effectif',
      percentage: '%',
      noData: 'Aucune donnée disponible',
    },
    en: {
      title: 'Dashboard - Student Enrollment',
      subtitle: 'Learners overview',
      totalStudents: 'Total students',
      boys: 'Boys',
      girls: 'Girls',
      byStatus: 'Distribution by status',
      byGender: 'Distribution by gender',
      byLevel: 'Distribution by level',
      bySection: 'Distribution by section',
      byCycle: 'Distribution by cycle',
      byClass: 'Distribution by class',
      transferred: 'Transferred',
      expelled: 'Expelled',
      socialCases: 'Social cases',
      handicaps: 'Disabilities',
      loading: 'Loading...',
      error: 'Loading error',
      distribution: 'Distribution',
      details: 'View details',
      school: 'School',
      academicYear: 'Academic Year',
      noYear: 'No year selected',
      noSchool: 'No school selected',
      contextUpdated: 'School context updated — reloading stats',
      classTable: 'Detail by class',
      className: 'Class',
      count: 'Count',
      percentage: '%',
      noData: 'No data available',
    }
  }[lang]), [lang])

  const loadStats = useCallback(async (forceSchoolId?: number | null, forceYearId?: number | null) => {
    const currentSchoolId = forceSchoolId !== undefined ? forceSchoolId : schoolId
    const currentYearId = forceYearId !== undefined ? forceYearId : academicYearId

    console.log('[StudentsDashboard] loadStats called with:', { schoolId: currentSchoolId, yearId: currentYearId })
    setLoading(true)
    try {
      const params: Record<string, any> = {}
      if (currentSchoolId) params.school_id = currentSchoolId
      if (currentYearId) params.academic_year_id = currentYearId

      let res: any = null
      // V17 : /students/stats/summary = endpoint COMPLET (actifs uniquement +
      // tous les graphes). Appelé EN PREMIER car studentsApi.getStats pointe
      // vers l'ancien endpoint partiel (total non filtré + graphes vides).
      try {
        res = await apiService.get('/students/stats/summary', { params })
      } catch (e0) {
        console.warn('[StudentsDashboard] summary(params) failed:', e0)
        try { res = await studentsApi.getStats(params) }
        catch (e1) {
          console.warn('[StudentsDashboard] getStats(params) failed:', e1)
          try { res = await studentsApi.getStats() }
          catch (e2) {
            console.warn('[StudentsDashboard] getStats() failed:', e2)
            res = await apiService.get('/students/stats/summary')
          }
        }
      }

      const body = res?.data ?? res
      console.log('[StudentsDashboard] RAW response:', body)
      setRawResponse(body)
      const normalized = normalizeStats(body)
      console.log('[StudentsDashboard] Normalized stats:', normalized)
      setStats(normalized)
    } catch (err) {
      console.error('Erreur chargement stats:', err)
      toast.error($t.error)
      setStats(null)
    } finally {
      setLoading(false)
    }
  }, [schoolId, academicYearId, $t.error])

  useEffect(() => { loadStats() }, [])

  useEffect(() => {
    if (schoolId !== null || academicYearId !== null) loadStats()
  }, [schoolId, academicYearId, loadStats])

  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail?.year_id) return
      const newYearId = Number(detail.year_id)
      const newSchoolId = detail?.school_id !== undefined ? Number(detail.school_id) : null
      const newLabel = detail.label || getStoredYearLabel() || ''
      let changed = false
      if (newYearId && newYearId !== academicYearId) { setAcademicYearId(newYearId); setAcademicYearName(newLabel); changed = true }
      if (newSchoolId !== null && !isNaN(newSchoolId) && newSchoolId !== schoolId) { setSchoolId(newSchoolId); changed = true }
      if (changed) { toast.info($t.contextUpdated); loadStats(newSchoolId ?? schoolId, newYearId || academicYearId) }
    }
    const handleStorageChange = (e: StorageEvent) => {
      let changed = false, newYearId = academicYearId, newSchoolId = schoolId, newLabel = academicYearName
      if (e.key === SELECTED_YEAR_ID_KEY) { const p = e.newValue ? Number(e.newValue) : null; if (p && p !== academicYearId) { newYearId = p; changed = true } }
      if (e.key === SELECTED_YEAR_LABEL_KEY && e.newValue) { newLabel = e.newValue; changed = true }
      if (e.key === SELECTED_SCHOOL_ID_KEY) { const p = e.newValue ? Number(e.newValue) : null; if (p && p !== schoolId) { newSchoolId = p; changed = true } }
      if (changed) { setAcademicYearId(newYearId); setSchoolId(newSchoolId); setAcademicYearName(newLabel); toast.info($t.contextUpdated); loadStats(newSchoolId, newYearId) }
    }
    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange)
    window.addEventListener('storage', handleStorageChange)
    return () => { window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange); window.removeEventListener('storage', handleStorageChange) }
  }, [schoolId, academicYearId, academicYearName, $t.contextUpdated, loadStats])

  useEffect(() => {
    const syncFromStorage = () => {
      const storedYearId = getStoredYearId()
      const storedSchoolId = getStoredSchoolId()
      const storedLabel = getStoredYearLabel() || ''
      let changed = false
      if (storedYearId && storedYearId !== academicYearId) { setAcademicYearId(storedYearId); setAcademicYearName(storedLabel); changed = true }
      if (storedSchoolId && storedSchoolId !== schoolId) { setSchoolId(storedSchoolId); changed = true }
      if (changed) loadStats(storedSchoolId, storedYearId)
    }
    const interval = setInterval(syncFromStorage, 2000)
    return () => clearInterval(interval)
  }, [schoolId, academicYearId, loadStats])

  // ─── DONNÉES CALCULÉES ───
  const totalStudents = stats?.total_students || 0
  const boysCount = stats?.by_sex?.M || 0
  const girlsCount = stats?.by_sex?.F || 0
  const socialCasesCount = stats?.social_cases || 0
  const handicapsCount = stats?.handicaps || 0

  const statusData = useMemo(() => {
    if (!stats?.by_status) return [] as StatItem[]
    return Object.entries(stats.by_status)
      .map(([key, value]: [string, any]) => ({
        name: key.charAt(0).toUpperCase() + key.slice(1),
        count: Number(value),
        color: STATUS_COLORS[key.toLowerCase()] || CHART_COLORS[Object.keys(stats.by_status).indexOf(key) % CHART_COLORS.length]
      }))
      .filter(d => d.count > 0)
      .sort((a, b) => b.count - a.count)
  }, [stats])

  const genderData = useMemo(() => [
    { label: lang === 'fr' ? 'Garçons' : 'Boys', value: boysCount, color: '#3b82f6' },
    { label: lang === 'fr' ? 'Filles' : 'Girls', value: girlsCount, color: '#ec4899' },
  ], [boysCount, girlsCount, lang])

  const levelData = useMemo(() => (stats?.by_level || []).slice(0, 10), [stats])
  const sectionData = useMemo(() => (stats?.by_section || []).slice(0, 10), [stats])
  const cycleData = useMemo(() => (stats?.by_cycle || []).slice(0, 10), [stats])
  const classData = useMemo(() => (stats?.by_class || []).slice(0, 15), [stats])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">{$t.loading}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-amber-50/10 p-6">
      {/* HEADER */}
      <div className="page-header bg-white/80 backdrop-blur-md border-b border-blue-100 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="page-title text-2xl font-bold bg-gradient-to-r from-blue-700 to-cyan-600 bg-clip-text text-transparent">
                {$t.title}
              </h1>
              <p className="page-subtitle text-sm text-gray-500 mt-0.5">{$t.subtitle}</p>
              <div className="flex items-center gap-2 mt-1.5">
                {schoolId && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex items-center gap-1">
                    <Landmark className="w-3 h-3" />{$t.school} ID: {schoolId}
                  </span>
                )}
                {academicYearId && academicYearName && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                    <Calendar className="w-3 h-3" />{academicYearName}
                  </span>
                )}
                {!schoolId && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />{$t.noSchool}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/students')} className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-md shadow-blue-200 flex items-center gap-2">
              <Users className="w-4 h-4" />{lang === 'fr' ? 'Liste des élèves' : 'Student list'}
            </button>
          </div>
        </div>
      </div>

      {/* STATS CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <StatCard label={$t.totalStudents} value={totalStudents.toLocaleString()} icon={Users} color="bg-gradient-to-br from-blue-500 to-blue-600" change="+5.2%" onClick={() => navigate('/students')} />
        <StatCard label={$t.boys} value={boysCount.toLocaleString()} icon={Users} color="bg-gradient-to-br from-sky-500 to-blue-500" change={`${totalStudents > 0 ? Math.round((boysCount / totalStudents) * 100) : 0}%`} />
        <StatCard label={$t.girls} value={girlsCount.toLocaleString()} icon={Users} color="bg-gradient-to-br from-pink-500 to-rose-500" change={`${totalStudents > 0 ? Math.round((girlsCount / totalStudents) * 100) : 0}%`} />
        <StatCard label={$t.socialCases} value={socialCasesCount} icon={HeartPulse} color="bg-gradient-to-br from-purple-500 to-violet-500" subLabel={`${totalStudents > 0 ? ((socialCasesCount / totalStudents) * 100).toFixed(1) : 0}% du total`} />
        <StatCard label={$t.handicaps} value={handicapsCount} icon={AlertCircle} color="bg-gradient-to-br from-amber-500 to-orange-500" subLabel={`${totalStudents > 0 ? ((handicapsCount / totalStudents) * 100).toFixed(1) : 0}% du total`} />
        <StatCard label={$t.transferred} value={stats?.by_status?.transfere || stats?.by_status?.TRANSFERE || 0} icon={ArrowLeftRight} color="bg-gradient-to-br from-cyan-500 to-teal-500" />
      </div>

      {/* LIGNE 1: GENRE + STATUT */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-pink-600" />
              <h3 className="font-semibold text-gray-800">{$t.byGender}</h3>
            </div>
            <span className="text-xs text-gray-400">{$t.distribution}</span>
          </div>
          <div className="p-5 h-72">
            <PieChartSVG data={genderData} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-800">{$t.byStatus}</h3>
            </div>
            <span className="text-xs text-gray-400">{$t.distribution}</span>
          </div>
          <div className="p-5 h-72">
            {statusData.length > 0 ? (
              <BarChartSVG data={statusData} maxValue={Math.max(...statusData.map(d => d.count), 1)} />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Activity className="w-10 h-10 mb-2" /><p>{$t.noData}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* LIGNE 2: NIVEAU + SECTION */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-gray-800">{$t.byLevel}</h3>
            </div>
            <span className="text-xs text-gray-400">{levelData.length} {$t.distribution}</span>
          </div>
          <div className="p-5 h-80 overflow-y-auto">
            <HorizontalBarChart data={levelData} maxValue={Math.max(...levelData.map(d => d.count), 1)} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-gray-800">{$t.bySection}</h3>
            </div>
            <span className="text-xs text-gray-400">{sectionData.length} {$t.distribution}</span>
          </div>
          <div className="p-5 h-80 overflow-y-auto">
            <HorizontalBarChart data={sectionData} maxValue={Math.max(...sectionData.map(d => d.count), 1)} />
          </div>
        </div>
      </div>

      {/* LIGNE 3: CYCLE + CLASSE */}
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-orange-600" />
              <h3 className="font-semibold text-gray-800">{$t.byCycle}</h3>
            </div>
            <span className="text-xs text-gray-400">{cycleData.length} {$t.distribution}</span>
          </div>
          <div className="p-5 h-80 overflow-y-auto">
            <HorizontalBarChart data={cycleData} maxValue={Math.max(...cycleData.map(d => d.count), 1)} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-600" />
              <h3 className="font-semibold text-gray-800">{$t.byClass}</h3>
            </div>
            <span className="text-xs text-gray-400">{classData.length} {$t.distribution}</span>
          </div>
          <div className="p-5 h-80 overflow-y-auto">
            <HorizontalBarChart data={classData} maxValue={Math.max(...classData.map(d => d.count), 1)} />
          </div>
        </div>
      </div>

      {/* TABLEAU RÉCAPITULATIF PAR CLASSE */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-800">{$t.classTable}</h3>
          </div>
          <button onClick={() => navigate('/students')} className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
            {$t.details} <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{$t.className}</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">{$t.count}</th>
                <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">{$t.percentage}</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-48">{$t.distribution}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {classData.map((item, i) => {
                const pct = totalStudents > 0 ? Math.round((item.count / totalStudents) * 100) : 0
                const barColor = CHART_COLORS[i % CHART_COLORS.length]
                return (
                  <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3 text-gray-400 text-xs">{i + 1}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: barColor }} />
                        <span className="font-medium text-gray-700">{item.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold text-gray-900">{item.count}</td>
                    <td className="px-5 py-3 text-right text-gray-500">{pct}%</td>
                    <td className="px-5 py-3">
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                      </div>
                    </td>
                  </tr>
                )
              })}
              {classData.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-400">{$t.noData}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}